import type en from '../en/auth'
import type { Translation } from '../types'

/** Signing in, passwords, two-factor sign-in and the sessions of an account */
const auth: Translation<typeof en> = {
  "auth.tagline": "Plataforma de gestión de redes",
  "auth.ssoCouldNotStart": "No se pudo iniciar el inicio de sesión único",
  "auth.loginFailed": "Error al iniciar sesión",
  "auth.idleSignedOut": "Se cerró su sesión tras un tiempo sin actividad. Inicie sesión de nuevo para continuar.",
  "auth.emailPlaceholder": "usted@empresa.es",
  "auth.signIn": "Iniciar sesión",
  "auth.signInSso": "Acceder con inicio de sesión único",
  "auth.ssoByEmailHint": "Su dirección de trabajo indica con qué empresa inicia sesión.",
  "auth.continueSso": "Continuar con inicio de sesión único",
  "auth.forgotPassword": "¿Ha olvidado su contraseña?",
  "auth.resetFailed": "No se pudo enviar el correo de restablecimiento",
  "auth.setFailed": "No se pudo establecer la contraseña",
  "auth.resetTitle": "Restablecer contraseña",
  "auth.setTitle": "Establezca su contraseña",
  "auth.resetSubtitle": "Introduzca su correo electrónico para recibir un enlace de restablecimiento",
  "auth.setSubtitle": "Cree una contraseña segura para su cuenta",
  "auth.checkEmail": "Revise su correo electrónico",
  "auth.passwordSet": "Contraseña establecida",
  "auth.resetSent": "Si esa dirección existe, se ha enviado un enlace de restablecimiento.",
  "auth.accountActive": "Su cuenta ya está activa. Puede iniciar sesión.",
  "auth.goToLogin": "Ir al inicio de sesión",
  "auth.sendResetLink": "Enviar enlace de restablecimiento",
  "auth.backToLogin": "Volver al inicio de sesión",
  "auth.newPassword": "Nueva contraseña",
  "auth.confirmPassword": "Confirme la contraseña",
  "auth.check.length": "Al menos 12 caracteres",
  "auth.check.upper": "Letra mayúscula",
  "auth.check.lower": "Letra minúscula",
  "auth.check.number": "Número",
  "auth.check.match": "Las contraseñas coinciden",
  "auth.setAndActivate": "Establecer contraseña y activar cuenta",
  "auth.landing.title": "¿Adónde desea ir?",
  "auth.landing.signedInAs": "Sesión iniciada como {email}",
  "auth.landing.noAccess": "Todavía no tiene acceso a ninguna empresa. Pida a un administrador que le añada.",
  "auth.landing.platform": "Administración de la plataforma",
  "auth.landing.platformSub": "Empresas, licencias, proxy perimetral y configuración",
  "auth.landing.companyAdmin": "Administrador de la empresa",
  "auth.landing.member": "Miembro",
  "auth.ssoIncomplete": "No se pudo completar el inicio de sesión. Inicie sesión de nuevo.",
  "auth.backToSignIn": "Volver al inicio de sesión",
  "auth.signingIn": "Iniciando sesión…",
  "auth.reauth.title": "Confirme que es usted",
  "auth.reauth.sso": "Este cambio requiere que vuelva a iniciar sesión en su proveedor de identidad (como máximo cada 10 minutos). Después, vuelva a realizar el cambio.",
  "auth.reauth.idpUnreachable": "No se pudo contactar con el proveedor de identidad",
  "auth.reauth.password": "Este cambio requiere de nuevo su contraseña. No se le volverá a pedir durante los próximos 10 minutos.",
  "auth.tf.startAgain": "← Empezar de nuevo",
  "auth.tf.setupTitle": "Configurar el inicio de sesión en dos pasos",
  "auth.tf.setupIntro": "Su cuenta necesita un código de una aplicación de autenticación en cada inicio de sesión. Configúrela una vez, ahora.",
  "auth.tf.title": "Inicio de sesión en dos pasos",
  "auth.tf.enterRecovery": "Introduzca uno de sus códigos de recuperación. Cada uno funciona una vez.",
  "auth.tf.enterCode": "Introduzca el código de 6 dígitos que muestra ahora su aplicación de autenticación.",
  "auth.tf.recoveryCode": "Código de recuperación",
  "auth.tf.code": "Código",
  "auth.tf.verify": "Verificar",
  "auth.tf.useApp": "Usar un código de la aplicación",
  "auth.tf.lostPhone": "¿Ha perdido el teléfono? Use un código de recuperación",
  "auth.tf.couldNotStart": "No se pudo iniciar la configuración",
  "auth.tf.savedContinue": "Los he guardado — continuar",
  "auth.tf.saved": "Los he guardado",
  "auth.tf.step1": "Abra una aplicación de autenticación en su teléfono (Google Authenticator, Microsoft Authenticator, 1Password, …).",
  "auth.tf.step2": "Escanee el código QR o escriba la clave.",
  "auth.tf.step3": "Introduzca el código de 6 dígitos que muestra la aplicación.",
  "auth.tf.qrAlt": "Código QR con la clave, para escanear con su aplicación de autenticación",
  "auth.tf.key": "Clave",
  "auth.tf.codeFromApp": "Código de la aplicación",
  "auth.tf.turnOn": "Activar",
  "auth.tf.saveCodes": "Guarde estos códigos de recuperación en un lugar seguro, como un gestor de contraseñas. Cada uno le permite iniciar sesión una vez si pierde el teléfono. Solo se muestran ahora.",
  "auth.tf.recoveryCodes": "Códigos de recuperación",
  "auth.tf.codesCopied": "Códigos de recuperación copiados",
  "auth.tf.copyHint": "Seleccione los códigos y cópielos, o descárguelos.",
  "auth.prot.ssoLinked": "Inicio de sesión único vinculado",
  "auth.prot.ssoLinkedText": "Ya puede iniciar sesión con su proveedor de identidad.",
  "auth.prot.couldNotStart": "No se pudo iniciar",
  "auth.prot.title": "Protección del inicio de sesión",
  "auth.prot.idleLabel": "Cerrar mi sesión tras un tiempo sin actividad",
  "auth.prot.afterMax": "Tras {duration} (lo máximo permitido aquí)",
  "auth.prot.after": "Tras {duration}",
  "auth.prot.never": "Nunca",
  "auth.prot.staySignedIn": "Su sesión sigue iniciada mientras no usa el panel, hasta que alcanza su duración máxima.",
  "auth.prot.signedOutAfter": "Una sesión que deja abierta se cierra {duration} después de su último uso.",
  "auth.prot.newDeviceMail": "Enviarme un correo cuando mi cuenta inicie sesión desde un dispositivo que no haya usado antes",
  "auth.prot.sso": "Inicio de sesión único",
  "auth.prot.linked": "Vinculado",
  "auth.prot.linkedText": "Puede iniciar sesión con el proveedor de identidad de su empresa.",
  "auth.prot.linkText": "Su empresa inicia sesión a través de su proveedor de identidad. Vincule su cuenta para usarlo.",
  "auth.prot.linkMine": "Vincular mi cuenta",
  "auth.sec.isOff": "El inicio de sesión en dos pasos está desactivado",
  "auth.sec.isOn": "El inicio de sesión en dos pasos está activado",
  "auth.sec.signedOutDevice": "Sesión cerrada en ese dispositivo",
  "auth.sec.signedOutOthers": { one: "Sesión cerrada en {count} dispositivo más", other: "Sesión cerrada en {count} dispositivos más" },
  "auth.sec.couldNotSignOut": "No se pudo cerrar la sesión",
  "auth.sec.newSetNote": "Un nuevo conjunto sustituye al anterior: los códigos antiguos dejan de funcionar.",
  "auth.sec.offNote": "Para iniciar sesión solo volverá a necesitar su contraseña.",
  "auth.sec.codeFromYourApp": "Código de su aplicación de autenticación",
  "auth.sec.newCodes": "Nuevos códigos de recuperación",
  "auth.sec.turnOff": "Desactivar",
  "auth.sec.on": "Activado",
  "auth.sec.since": { one: "desde el {date} · queda {count} código de recuperación", other: "desde el {date} · quedan {count} códigos de recuperación" },
  "auth.sec.required": " · obligatorio para su cuenta",
  "auth.sec.offText": "Desactivado. Con el inicio de sesión en dos pasos, una contraseña robada no basta: para iniciar sesión también se necesita un código de una aplicación de su teléfono.",
  "auth.sec.whereSignedIn": "Dónde tiene la sesión iniciada",
  "auth.sec.thisDevice": "Este dispositivo",
  "auth.sec.signedInWhen": "sesión iniciada {when}",
  "auth.sec.activeWhen": "activa {when}",
  "auth.sec.endsDate": "termina el {date}",
  "auth.sec.signOutDevice": "Cerrar la sesión de {device}, iniciada {when}",
  "auth.sec.confirmSignOutAll": "¿Cerrar la sesión en todos los demás dispositivos?",
  "auth.sec.signOutOthers": "Cerrar sesión en otros dispositivos",
  "auth.landing.client": "Eventos de {customer}",
  "auth.landing.clientOpens": "Eventos de {customer} — abre el {when}",
  "auth.landing.clientNoEvent": "Eventos de {customer} — abre con el próximo evento",
  "auth.clientWindowClosed": "El inicio de sesión solo está abierto durante su evento",
  "auth.app.stayPrompt": "Mantener la sesión iniciada en este teléfono",
  "auth.app.unlockReason": "Desbloquear Velanox",
  "auth.app.unlock": "Desbloquear",
  "auth.app.changeServer": "Cambiar de servidor",
  "auth.app.offline": "No tiene conexión",
}

export default auth
